import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, HelpCircle, Loader2, Map as MapIcon, Mic, Monitor, Plus, RefreshCw, Rocket, Sparkles,
  Square, Target, Trash2, X,
} from 'lucide-react';
import { useAuth } from '../Auth';
import { apiService } from '../../services/api';
import useKickoffSession from './useKickoffSession';
import useProjectData from '../ProjectPage/useProjectData';
import ProjectOffers from '../ProjectPage/tabs/ProjectOffers';
import { Avatar, Card, Label, euros, hours } from '../ProjectPage/ui';
import { DateField, InlineArea, InlineText } from '../ProjectPage/inline';
import { iterationColors } from '../ProjectPage/roadmap/colors';
import { shortDay } from '../ProjectPage/roadmap/changes';
import { toDay, toIso } from '../ProjectPage/roadmap/dates';
import { LevelMeter, useLiveAudio } from '../Holacracy/LiveSecretary';
import { liveCaptureSupported } from '../Meetings/liveCapture';
import { FIELD_LABELS, defaultRange, iterationValues, suggestions, toIterationFields } from './launchSuggestions';

// Kickoff de lancement d'un projet en proposition (Rémy, 2026-10-22) : quelqu'un présente le projet à l'équipe qui va
// le conduire, Nexus écoute et propose au fil de la réunion le brief, la vision et la roadmap (dates, budgets, vision
// de chaque itération), en partant de la proposition commerciale. Chaque proposition se prend ou s'écarte ; tout reste
// modifiable à la main, et la page de chacun suit. Clore le kickoff passe le projet « En cours » et enregistre son
// état de départ : l'historique de la roadmap et du budget commence là.

const shown = (field, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'start_date' || field === 'end_date') return shortDay(value);
  if (field === 'hours') return hours(value);
  if (field === 'purchases_eur' || field === 'ca_eur') return euros(value);
  const text = String(value);
  return `« ${text.length > 140 ? `${text.slice(0, 140)}…` : text} »`;
};

const clock = (iso) => {
  const date = iso ? new Date(String(iso).endsWith('Z') ? iso : `${iso}Z`) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
};

const Proposal = ({ children, onTake, onDismiss, busy, disabled, takeLabel = 'Prendre' }) => (
  <div className="mt-2 flex items-start gap-2 rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2">
    <Sparkles size={14} className="mt-0.5 shrink-0 text-indigo-500" />
    <div className="min-w-0 flex-1 text-[13px] leading-snug text-indigo-950">{children}</div>
    <div className="flex shrink-0 items-center gap-1">
      <button type="button" onClick={onTake} disabled={busy || disabled}
        className="flex items-center gap-1 rounded-lg bg-indigo-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
        {busy && <Loader2 size={11} className="animate-spin" />} {takeLabel}
      </button>
      <button type="button" onClick={onDismiss} disabled={busy} aria-label="Écarter"
        title="Écarter : Nexus ne la reproposera pas telle quelle"
        className="rounded-lg p-1 text-indigo-400 hover:bg-indigo-100 hover:text-indigo-700 disabled:opacity-50"><X size={13} /></button>
    </div>
  </div>
);

const NumberField = ({ value, onSave, suffix, ariaLabel, readOnly }) => {
  const current = value ? String(Math.round(Number(value))) : '';
  const [draft, setDraft] = useState(current);
  useEffect(() => { setDraft(current); }, [current]);
  const commit = () => {
    const next = draft.trim() === '' ? 0 : Number(draft);
    if (Number.isNaN(next) || next < 0) { setDraft(current); return; }
    if (Math.round(next) !== Math.round(Number(value) || 0)) onSave(next);
  };
  return (
    <label className="flex items-baseline gap-1 text-xs text-slate-400">
      <input type="number" min="0" step="1" value={draft} aria-label={ariaLabel} readOnly={readOnly} placeholder="0"
        onChange={(e) => setDraft(e.target.value)} onBlur={readOnly ? undefined : commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="w-20 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-right text-xs tabular-nums text-slate-700 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 read-only:border-transparent read-only:bg-transparent" />
      {suffix}
    </label>
  );
};

// Frise de la roadmap : itérations de la fiche en plein, propositions nouvelles de Nexus en pointillé.
const Timeline = ({ iterations, additions, colors }) => {
  const bars = [
    ...iterations.filter((it) => it.start_date && it.end_date).map((it) => ({
      key: `it-${it.id}`, name: it.name, start: toDay(it.start_date), end: toDay(it.end_date), color: colors.get(it.id),
    })),
    ...additions.filter((a) => a.item.start_date && a.item.end_date).map((a) => ({
      key: a.key, name: a.item.name, start: toDay(a.item.start_date), end: toDay(a.item.end_date), ghost: true,
    })),
  ].sort((a, b) => a.start - b.start);
  if (!bars.length) return null;
  const from = Math.min(...bars.map((b) => b.start));
  const span = Math.max(1, Math.max(...bars.map((b) => b.end)) - from);
  return (
    <div className="mb-3 space-y-1 rounded-xl bg-slate-50 p-2">
      {bars.map((bar) => (
        <div key={bar.key} className="relative h-5">
          <div title={bar.name}
            className={`absolute inset-y-0 flex items-center overflow-hidden rounded-md px-1.5 text-[10px] font-semibold transition-all duration-500 ${bar.ghost
              ? 'border border-dashed border-indigo-300 bg-indigo-50 text-indigo-700'
              : `${bar.color?.soft || 'bg-sky-100'} ${bar.color?.text || 'text-sky-800'}`}`}
            style={{ left: `${((bar.start - from) / span) * 100}%`, width: `${Math.max(3, ((bar.end - bar.start) / span) * 100)}%` }}>
            <span className="truncate">{bar.name}</span>
          </div>
        </div>
      ))}
      <div className="flex justify-between px-0.5 text-[10px] tabular-nums text-slate-400">
        <span>{shortDay(toIso(from))}</span>
        <span>{shortDay(toIso(from + span))}</span>
      </div>
    </div>
  );
};

const IterationRow = ({ iteration, color, update, readOnly, busy, onPatch, onRemove, onTake, onDismiss }) => {
  const values = iterationValues(iteration);
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${color?.dot || 'bg-sky-500'}`} />
        <InlineText value={iteration.name} required ariaLabel="Nom de l'itération" readOnly={readOnly}
          onSave={(name) => onPatch({ name })} className="min-w-[8rem] flex-1 text-sm font-semibold text-slate-800" />
        <div className="flex items-center gap-1">
          <DateField value={values.start_date} deferred readOnly={readOnly} ariaLabel="Début" onSave={(start_date) => onPatch({ start_date })} />
          <span className="text-xs text-slate-300">→</span>
          <DateField value={values.end_date} deferred readOnly={readOnly} ariaLabel="Fin" onSave={(end_date) => onPatch({ end_date })} />
        </div>
        {!readOnly && (
          <button type="button" onClick={onRemove} aria-label="Supprimer l'itération" title="Supprimer l'itération"
            className="rounded-md p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600"><Trash2 size={13} /></button>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 pl-5">
        <NumberField value={values.hours} suffix="h" ariaLabel="Budget temps" readOnly={readOnly}
          onSave={(v) => onPatch({ forecast_time_hours: v })} />
        <NumberField value={values.purchases_eur} suffix="€ d’achats" ariaLabel="Budget achats" readOnly={readOnly}
          onSave={(v) => onPatch({ forecast_purchase_eur: v })} />
        <NumberField value={values.ca_eur} suffix="€ de CA" ariaLabel="CA" readOnly={readOnly}
          onSave={(v) => onPatch({ forecast_ca_eur: v })} />
      </div>
      <div className="pl-5">
        <InlineArea value={iteration.description} readOnly={readOnly} rows={1}
          placeholder="Vision de l’itération : ce qu’on veut avoir obtenu à la fin" onSave={(description) => onPatch({ description })} />
      </div>
      {update && (
        <div className="pl-5">
          <Proposal busy={busy} disabled={readOnly} onTake={onTake} onDismiss={onDismiss}>
            <span className="font-semibold">Nexus propose : </span>
            {Object.entries(update.fields).map(([field, value], index) => (
              <span key={field}>
                {index > 0 && ' · '}
                {FIELD_LABELS[field].toLowerCase()} {field === 'vision' || field === 'name' ? '' : `${shown(field, update.current[field])} → `}
                <span className="font-semibold">{shown(field, value)}</span>
              </span>
            ))}
          </Proposal>
        </div>
      )}
    </li>
  );
};

const NewIteration = ({ addition, busy, readOnly, onTake, onDismiss }) => {
  const { item } = addition;
  return (
    <li className="py-2">
      <div className="rounded-xl border border-dashed border-indigo-300 bg-indigo-50/40 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Sparkles size={14} className="shrink-0 text-indigo-500" />
          <p className="min-w-0 flex-1 text-sm font-semibold text-indigo-950">{item.name}</p>
          <span className="text-xs tabular-nums text-indigo-700">
            {item.start_date && item.end_date ? `${shortDay(item.start_date)} → ${shortDay(item.end_date)}` : 'dates à préciser'}
          </span>
        </div>
        <p className="mt-1 pl-6 text-xs tabular-nums text-indigo-800">
          {[item.hours != null && hours(item.hours), item.purchases_eur != null && `${euros(item.purchases_eur)} d’achats`,
            item.ca_eur != null && `${euros(item.ca_eur)} de CA`].filter(Boolean).join(' · ') || 'Budgets à préciser'}
        </p>
        {item.vision && <p className="mt-1 pl-6 text-[13px] leading-snug text-indigo-950">{item.vision}</p>}
        <div className="mt-2 flex justify-end gap-1">
          <button type="button" onClick={onDismiss} disabled={busy}
            className="rounded-lg px-2 py-1 text-[11px] font-semibold text-indigo-500 hover:bg-indigo-100 disabled:opacity-50">Écarter</button>
          <button type="button" onClick={onTake} disabled={busy || readOnly}
            className="flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Ajouter à la roadmap
          </button>
        </div>
      </div>
    </li>
  );
};

const KickoffLaunchPage = () => {
  const { projectId } = useParams();
  const id = Number(projectId);
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const data = useProjectData(id, user);
  const { project, iterations, domains, actions, reload } = data;
  const inProposal = project?.status === 'proposition';
  const session = useKickoffSession({ projectId: id, user, getAuthHeaders, kind: 'lancement', enabled: inProposal });
  const kickoffId = session.kickoffSession?.id;
  const audio = useLiveAudio({ apiUrl: apiService.getBaseUrl(), meetingId: kickoffId, getAuthHeaders,
    path: kickoffId ? `/kickoffs/${kickoffId}/live-audio` : undefined });
  const [source, setSource] = useState('salle');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [finishedHere, setFinishedHere] = useState(false);
  const live = session.live || {};
  const draft = session.draft;
  const readOnly = domains?.roadmap?.can === false;
  const finished = finishedHere || Boolean(session.finished);

  // Un participant a modifié la fiche : on la recharge.
  const { refreshToken, notifyRefresh } = session;
  useEffect(() => {
    if (!refreshToken) return;
    reload.project();
    reload.iterations();
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (session.finished) { audio.stop(); reload.project(); }
  }, [session.finished]); // eslint-disable-line react-hooks/exhaustive-deps

  const colors = useMemo(() => iterationColors(iterations), [iterations]);
  const sugg = useMemo(() => suggestions(draft, project, iterations), [draft, project, iterations]);
  const totals = useMemo(() => iterations.map(iterationValues).reduce((sum, it) => ({
    hours: sum.hours + it.hours, purchases: sum.purchases + it.purchases_eur, ca: sum.ca + it.ca_eur,
  }), { hours: 0, purchases: 0, ca: 0 }), [iterations]);

  const run = useCallback(async (key, action) => {
    setBusy(key);
    setError(null);
    try {
      await action();
      notifyRefresh?.('fiche');
    } catch (err) {
      if (!err?.cancelled) setError(err?.message || 'Action impossible.');
    } finally {
      setBusy(null);
    }
  }, [notifyRefresh]);

  const decide = useCallback(async (body) => {
    const saved = await apiService.post(`/kickoffs/${kickoffId}/draft/decide`, body);
    if (saved) session.setDraft(saved);
  }, [kickoffId, session]);

  const takeText = (field, value) => run(field, () => actions.patchProject({ [field]: value }));
  const takeUpdate = (update) => run(update.key, () => actions.patchIteration(update.id, toIterationFields(update.fields)));
  const takeAddition = (addition, list = iterations) => run(addition.key, async () => {
    const fields = toIterationFields(addition.item);
    const range = fields.start_date && fields.end_date ? {} : defaultRange(list);
    const created = await actions.addIteration({ description: '', ...range, ...fields });
    if (created?.id) await decide({ key: addition.key, decision: 'applied', iteration_id: created.id, name: addition.item.name });
  });
  const dismiss = (key, value, label) => run(key, () => decide({ key, value: value == null ? null : String(value), decision: 'dismissed', label }));
  const takeAll = async () => {
    if (sugg.brief) await takeText('brief', sugg.brief);
    if (sugg.vision) await takeText('vision', sugg.vision);
    for (const update of sugg.updates) await takeUpdate(update); // eslint-disable-line no-await-in-loop
    for (const addition of sugg.additions) await takeAddition(addition); // eslint-disable-line no-await-in-loop
  };
  const addManual = () => run('manual', () => actions.addIteration({ ...defaultRange(iterations), description: '' }));
  const removeIteration = (iteration) => {
    if (!window.confirm(`Supprimer l’itération « ${iteration.name} » ?`)) return;
    run(`del-${iteration.id}`, () => actions.deleteIteration(iteration.id));
  };
  const askNexus = () => run('refresh', () => apiService.post(`/kickoffs/${kickoffId}/draft/refresh`, {}));

  const finish = async () => {
    const pending = sugg.count;
    const lines = [
      `Clore le kickoff : « ${project?.name || 'le projet'} » passe En cours.`,
      `On garde son état de départ : ${iterations.length} itération${iterations.length > 1 ? 's' : ''}, ${hours(totals.hours)}, ${euros(totals.purchases)} d’achats, CA ${euros(totals.ca)}. L’historique de la roadmap et du budget commence là.`,
    ];
    if (pending) lines.push(`${pending} proposition${pending > 1 ? 's' : ''} de Nexus n’${pending > 1 ? 'ont' : 'a'} pas été prise${pending > 1 ? 's' : ''} : elle${pending > 1 ? 's' : ''} sera laissée${pending > 1 ? 's' : ''} de côté.`);
    if (!window.confirm(lines.join('\n\n'))) return;
    setFinishing(true);
    try {
      audio.stop();
      await apiService.post(`/kickoffs/${kickoffId}/finish`, {});
      setFinishedHere(true);
      reload.project();
    } catch (err) {
      setError(err?.message || 'Le kickoff n’a pas pu être clos.');
    } finally {
      setFinishing(false);
    }
  };

  const capturing = audio.status === 'live' || audio.status === 'starting';
  const presence = [...new Map((session.connectedUsers || []).map((p) => [String(p.id || p.name), p])).values()];
  const questions = draft?.draft?.questions || [];

  if (project && !inProposal && !finished) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <p className="text-sm text-slate-600">« {project.name} » n’est plus en proposition : son kickoff de lancement est fait.</p>
        <div className="mt-4 flex justify-center gap-2">
          <button type="button" onClick={() => navigate(`/project/${id}`)} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white">Ouvrir le projet</button>
          <button type="button" onClick={() => navigate(`/project/${id}/kickoff`)} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">Sprints, tokens et responsables</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 pb-10">
      <div className="mx-auto w-full max-w-[1500px] px-3 pt-3 sm:px-4 lg:px-6">
        <header className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => navigate(`/project/${id}`)} aria-label="Retour au projet"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-slate-700"><ArrowLeft size={18} /></button>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">Kickoff de lancement</p>
            <h1 className="truncate text-xl font-semibold text-slate-900">{project?.name || '…'}</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {presence.length > 0 && (
              <div className="flex -space-x-1.5" title={presence.map((p) => p.name).join(', ')}>
                {presence.slice(0, 6).map((p, i) => <Avatar key={`${p.id || p.name}-${i}`} name={String(p.name || '?')} size="h-7 w-7" className="ring-2 ring-slate-50" />)}
              </div>
            )}
            {!finished && (
              <button type="button" onClick={finish} disabled={finishing || !kickoffId}
                title="Le projet passe En cours et son état de départ est enregistré"
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                {finishing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Clore le kickoff
              </button>
            )}
          </div>
        </header>

        {(error || data.error || session.kickoffError) && (
          <p className="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error || data.error || session.kickoffError}</p>
        )}
        {finished && (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <CheckCircle2 size={18} className="text-emerald-600" />
            <p className="min-w-0 flex-1 text-sm text-emerald-900">
              Kickoff clos{session.finished?.by ? ` par ${session.finished.by}` : ''}. Le projet est <span className="font-semibold">En cours</span> ;
              son état de départ est enregistré et l’historique de la roadmap commence.
            </p>
            <button type="button" onClick={() => navigate(`/project/${id}`)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">Ouvrir le projet</button>
            <button type="button" onClick={() => navigate(`/project/${id}/kickoff`)} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100">Sprints, tokens et responsables</button>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-4">
            <Card title="Le projet" icon={Target}>
              <Label>Brief</Label>
              <InlineArea value={project?.brief} readOnly={finished} placeholder="En deux phrases : ce que le projet doit apporter au client"
                onSave={(brief) => run('brief-edit', () => actions.patchProject({ brief }))} className="text-[15px] text-slate-800" rows={1} />
              {sugg.brief && !finished && (
                <Proposal busy={busy === 'brief'} onTake={() => takeText('brief', sugg.brief)} onDismiss={() => dismiss('brief', sugg.brief, 'Brief du projet')}>
                  {sugg.brief}
                </Proposal>
              )}
              <Label className="mt-4">Vision</Label>
              <InlineText value={project?.vision} readOnly={finished} placeholder="La vision du projet, en une ligne" ariaLabel="Vision du projet"
                onSave={(vision) => run('vision-edit', () => actions.patchProject({ vision }))} className="w-full text-sm text-slate-700" />
              {sugg.vision && !finished && (
                <Proposal busy={busy === 'vision'} onTake={() => takeText('vision', sugg.vision)} onDismiss={() => dismiss('vision', sugg.vision, 'Vision du projet')}>
                  {sugg.vision}
                </Proposal>
              )}
            </Card>

            <Card title="Roadmap" icon={MapIcon} action={(
              <span className="text-xs tabular-nums text-slate-500">
                {iterations.length} itération{iterations.length > 1 ? 's' : ''} · {hours(totals.hours)} · {euros(totals.purchases)} d’achats · CA {euros(totals.ca)}
              </span>
            )}>
              {draft?.proposal_ca > 0 && totals.ca > draft.proposal_ca * 1.02 && (
                <p className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Le CA de la roadmap ({euros(totals.ca)}) dépasse celui de la proposition ({euros(draft.proposal_ca)}) :
                  une itération porte peut-être deux fois le même travail.
                </p>
              )}
              <Timeline iterations={iterations} additions={finished ? [] : sugg.additions} colors={colors} />
              {iterations.length === 0 && !sugg.additions.length && (
                <p className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-sm text-slate-400">
                  Pas encore d’itération. Nexus en proposera d’après la proposition et ce qui se dit ; tu peux aussi en ajouter une.
                </p>
              )}
              <ul className="divide-y divide-slate-100">
                {iterations.map((iteration) => {
                  const update = finished ? null : sugg.updates.find((u) => u.id === iteration.id);
                  return (
                    <IterationRow key={iteration.id} iteration={iteration} color={colors.get(iteration.id)} update={update}
                      readOnly={readOnly || finished} busy={busy === update?.key}
                      onPatch={(fields) => run(`it-edit-${iteration.id}`, () => actions.patchIteration(iteration.id, fields))}
                      onRemove={() => removeIteration(iteration)} onTake={() => takeUpdate(update)}
                      onDismiss={() => dismiss(update.key, update.value, `Changement de « ${iteration.name} »`)} />
                  );
                })}
              </ul>
              {!finished && sugg.additions.length > 0 && (
                <ul className="mt-1">
                  {sugg.additions.map((addition) => (
                    <NewIteration key={addition.key} addition={addition} busy={busy === addition.key} readOnly={readOnly}
                      onTake={() => takeAddition(addition)} onDismiss={() => dismiss(addition.key, addition.item.name, `Itération « ${addition.item.name} »`)} />
                  ))}
                </ul>
              )}
              {!finished && !readOnly && (
                <button type="button" onClick={addManual} disabled={busy === 'manual'}
                  className="mt-2 flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50">
                  <Plus size={13} /> Itération
                </button>
              )}
              {readOnly && <p className="mt-2 text-xs text-slate-400">La roadmap est tenue par un autre rôle sur ce projet : seul son titulaire la modifie.</p>}
            </Card>
          </div>

          <div className="min-w-0 space-y-4">
            {!finished && (
              <Card title="Nexus" icon={Mic} action={sugg.count > 1 && !readOnly ? (
                <button type="button" onClick={takeAll} disabled={Boolean(busy)}
                  className="rounded-lg px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                  Tout prendre ({sugg.count})
                </button>
              ) : null}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`relative flex h-2.5 w-2.5 ${live.active ? '' : 'opacity-40'}`}>
                    {live.active && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />}
                    <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${live.active ? 'bg-rose-500' : 'bg-slate-400'}`} />
                  </span>
                  <p className="min-w-0 flex-1 text-sm text-slate-700">
                    {live.active
                      ? `Nexus écoute${live.started_by ? ` (son capté par ${live.started_by})` : ''} · ${Number(live.words || 0).toLocaleString('fr-FR')} mots entendus`
                      : live.words ? `En pause · ${Number(live.words).toLocaleString('fr-FR')} mots entendus` : 'Nexus n’écoute pas encore.'}
                  </p>
                  {capturing && <LevelMeter level={audio.level} />}
                  {capturing ? (
                    <button type="button" onClick={audio.stop}
                      className="flex items-center gap-1.5 rounded-lg border border-rose-100 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-100">
                      <Square size={12} /> Arrêter
                    </button>
                  ) : !live.active && (
                    <>
                      <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs font-semibold">
                        {[['salle', 'En salle', Mic], ['visio', 'Visio', Monitor]].map(([key, label, Icon]) => (
                          <button key={key} type="button" onClick={() => setSource(key)}
                            className={`flex items-center gap-1 rounded-md px-2 py-1 ${source === key ? 'bg-slate-800 text-white' : 'text-slate-500'}`}>
                            <Icon size={12} /> {label}
                          </button>
                        ))}
                      </div>
                      <button type="button" onClick={() => audio.start(source)}
                        disabled={!kickoffId || !live.mac_connected || !liveCaptureSupported()}
                        title={!live.mac_connected ? 'Le Mac d’Innowide n’est pas connecté' : undefined}
                        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                        {audio.status === 'starting' ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />} Écouter
                      </button>
                    </>
                  )}
                </div>
                {!live.active && !capturing && (
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">
                    {live.mac_connected
                      ? 'Un seul poste capte le son. Nexus écoute sur le Mac d’Innowide (rien ne sort de chez nous) et revoit sa proposition toutes les quarante secondes environ ; l’équipe prend ou écarte, et tout reste modifiable.'
                      : 'Le Mac d’Innowide n’est pas connecté : l’écoute est indisponible, mais Nexus peut proposer d’après la proposition commerciale.'}
                  </p>
                )}
                {((audio.error && (capturing || !live.active)) || live.warning) && (
                  <p className="mt-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-600">{live.warning || audio.error}</p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                  <p className="min-w-0 flex-1 text-xs text-slate-500">
                    {session.draftStatus?.updating ? (
                      <span className="flex items-center gap-1.5 text-indigo-700"><Loader2 size={12} className="animate-spin" /> Nexus revoit sa proposition…</span>
                    ) : session.draftStatus?.error ? (
                      <span className="text-rose-600">{session.draftStatus.error}</span>
                    ) : draft?.at ? (
                      <>Proposition revue à {clock(draft.at)}{draft.has_proposal ? ', d’après la proposition commerciale' : ''}{draft.words ? ` et ${Number(draft.words).toLocaleString('fr-FR')} mots de réunion` : ''}.
                        {sugg.count ? ` ${sugg.count} à prendre ou écarter.` : ' Tout est pris.'}</>
                    ) : 'Pas encore de proposition de Nexus.'}
                  </p>
                  <button type="button" onClick={askNexus} disabled={!kickoffId || session.draftStatus?.updating || busy === 'refresh'}
                    className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                    {draft?.at ? <RefreshCw size={12} /> : <Sparkles size={12} />} {draft?.at ? 'Revoir maintenant' : 'Proposer maintenant'}
                  </button>
                </div>
              </Card>
            )}

            {!finished && questions.length > 0 && (
              <Card title="À trancher pendant le kickoff" icon={HelpCircle}>
                <ul className="space-y-1.5">
                  {questions.map((question) => (
                    <li key={question} className="flex gap-2 text-sm leading-snug text-slate-700">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />{question}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {project && <ProjectOffers projectId={id} projectName={project.name} />}

            {!finished && (
              <Card title="Après le kickoff" icon={Rocket}>
                <p className="text-sm leading-relaxed text-slate-600">
                  Clore le kickoff passe le projet <span className="font-semibold">En cours</span> et garde son état de départ (dates, temps,
                  achats et CA des itérations) : l’historique de la roadmap et du budget commence là. Les sprints, tokens et
                  responsables se font ensuite, dans le kickoff d’équipe.
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KickoffLaunchPage;
